package com.fantasytenis.shared.domain.contests.entity

import com.fantasytenis.shared.domain.bracket.entity.LockOverride

/**
 * Contest domain entity.
 */
data class ContestEntity(
    val id: String,
    val name: String,
    val tournamentId: String,
    val tournamentName: String,
    val startTime: Long,
    val rosterLockTime: Long,
    val lineupLockTime: String, // ISO timestamp for display
    val entryFee: Double,
    val prizePool: Double,
    val maxEntries: Int,
    val currentEntries: Int,
    val status: ContestStatus,
    val contestType: ContestType,
    val salaryCap: Int,
    val rosterSize: Int,
    val prizeStructure: List<PrizeLevel>,
    val isGuaranteed: Boolean,
    val isFeatured: Boolean,
    // Lock override fields for admin manual control
    val lockOverride: LockOverride = LockOverride.NONE,
    val lockOverrideBy: String? = null,
    val lockOverrideAt: String? = null,
    val lockOverrideReason: String? = null,
    // Perfect lineup - calculated after contest completes
    val perfectLineup: PerfectLineupEntity? = null
) {
    val spotsRemaining: Int get() = maxEntries - currentEntries
    val isFull: Boolean get() = spotsRemaining <= 0

    /**
     * Determines if the contest is locked for submissions.
     * Admin override takes precedence over time-based lock.
     */
    val isLocked: Boolean
        get() = when (lockOverride) {
            LockOverride.FORCE_UNLOCKED -> false
            LockOverride.FORCE_LOCKED -> true
            LockOverride.NONE -> status != ContestStatus.UPCOMING
        }

    /** True if an admin has manually unlocked this contest */
    val isManuallyUnlocked: Boolean get() = lockOverride == LockOverride.FORCE_UNLOCKED

    /** True if an admin has manually locked this contest */
    val isManuallyLocked: Boolean get() = lockOverride == LockOverride.FORCE_LOCKED

    /** True if there is any lock override (not using time-based logic) */
    val hasLockOverride: Boolean get() = lockOverride != LockOverride.NONE

    /** Whether users can enter this contest (mirrors backend logic) */
    val canEnter: Boolean
        get() = if (isManuallyUnlocked) !isFull else (status == ContestStatus.UPCOMING && !isFull)
}

enum class ContestStatus {
    UPCOMING,
    LIVE,
    COMPLETED,
    CANCELLED
}

enum class ContestType {
    GPP,           // Guaranteed Prize Pool (large field)
    HEAD_TO_HEAD,  // 1v1
    FIFTY_FIFTY,   // Top 50% win
    MULTIPLIER,    // Double/triple up
    FREE           // Free entry
}

data class PrizeLevel(
    val minRank: Int,
    val maxRank: Int,
    val prize: Double
)

/**
 * Contest Entry entity.
 */
data class EntryEntity(
    val id: String,
    val contestId: String,
    val userId: String,
    val lineupId: String?,
    val entryNumber: Int,
    val totalPoints: Double,
    val rank: Int?,
    val payoutAmount: Double,
    val status: EntryStatus,
    val createdAt: Long,
    // User profile fields for leaderboard display
    val displayName: String? = null,
    val username: String? = null,
    val avatarUrl: String? = null
) {
    /** Formatted display name: @username if available, otherwise displayName or "Player" */
    val formattedDisplayName: String
        get() = username?.let { "@$it" } ?: displayName ?: "Player"

    /** Initials for avatar fallback */
    val initials: String
        get() {
            val name = displayName ?: username ?: "?"
            return name.split(" ")
                .mapNotNull { it.firstOrNull()?.uppercaseChar() }
                .take(2)
                .joinToString("")
                .ifEmpty { name.take(2).uppercase() }
        }
}

enum class EntryStatus {
    PENDING,      // 1 - Entry created but lineup not submitted
    CONFIRMED,    // 2 - Lineup submitted successfully
    LIVE,         // 3 - Contest is in progress
    FINISHED,     // 4 - Contest completed
    CANCELLED,    // 5 - Entry was cancelled
    PAID;         // 6 - Payout processed

    /** Entry is active and should be displayed on home screen */
    val isVisibleOnHome: Boolean get() = this in listOf(PENDING, CONFIRMED, LIVE)
}
